import { LocalOfferLocation } from '@varo/graphql-schema-types';
import { LocationObject } from 'expo-location';
import RewardsMapView from 'features/rewards/components/RewardsMap/RewardsMapView';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface RewardsMapProps {
  /** Unique identifier for the map */
  id: string;
  /** Section header text */
  sectionTitle: string;
  /** Array of location markers to display on the map */
  markerData: LocalOfferLocation[];
  /** Center position of the map */
  cameraLocation: google.maps.LatLngLiteral;
  /** Callback when a marker is pressed */
  onMarkerPress: (marker: LocalOfferLocation) => void;
  /** Externally controlled selected marker ID */
  selectedMarkerId?: string | null;
  /** Callback when the map location changes */
  onLocationChange?: (
    location: google.maps.LatLngLiteral | LocationObject,
    isInitialLoad?: boolean,
  ) => void;
  /** Callback when the section header or map is pressed (for navigation) */
  /** Callback when the map is pressed (when map is non-interactive, allowMapInteractions = false) */
  onSectionPress?: () => void;
  /** Callback when the map background is pressed */
  onMapTap?: () => void;
  /** Whether to allow user interactions with the map */
  allowMapInteractions?: boolean;
  /** Analytics ID for tracking interactions */
  analyticsId?: string;
  /** Custom attributes for analytics */
  customAttributes?: Record<string, string> | null;
  /** Whether to show the search this area button */
  shouldShowSearchThisAreaButtonVisible?: boolean;
  isLoading: boolean;
  defaultCameraZoom?: number;
  defaultCameraHeading?: number;
  onCameraLocationChange?: (
    location: google.maps.LatLngLiteral,
    zoom?: number,
    heading?: number,
  ) => void;
}

const RewardsMap = ({
  id,
  sectionTitle,
  markerData,
  cameraLocation,
  onMarkerPress,
  onLocationChange = () => {},
  onSectionPress,
  analyticsId = 'rewardsMapSection',
  customAttributes = null,
  isLoading = false,
  selectedMarkerId,
  onCameraLocationChange,
  onMapTap,
  defaultCameraZoom,
  defaultCameraHeading,
}: RewardsMapProps) => {
  const { analyticsEvents } = useAnalyticsConfiguration();

  const sectionHeaderAnalytics = usePressableAnalytics({
    analyticsId: `${analyticsId}HeaderPressed`,
    customAttributes,
  });

  const handleMarkerPress = (marker: LocalOfferLocation) => {
    if (selectedMarkerId !== marker.sourceMerchantId) {
      analyticsEvents.logPressableEventFromConfig(
        '/rewards/nearby-offers/map/offer-pin-selected',
        {
          merchant_name: marker.name ?? '',
          merchant_id: marker.sourceMerchantId ?? '',
          offer_bracket: 'nearby',
        },
      );
    }

    onMarkerPress(marker);
  };

  const handleSectionPress = () => {
    if (onSectionPress) {
      sectionHeaderAnalytics();
      onSectionPress();
    }
  };

  return (
    <View style={styles.container}>
      <RewardsMapView
        id={id}
        navigationBarTitle={sectionTitle}
        onRewardsLocationSelected={handleSectionPress}
        onMarkerPress={handleMarkerPress}
        isLoading={isLoading}
        onLocationChange={onLocationChange}
        markerData={markerData}
        requestLocationTitle=""
        requestLocationBody=""
        showMapSearchBar={false}
        shouldShowSearchThisAreaButtonVisible
        defaultCameraLocation={cameraLocation}
        defaultCameraZoom={defaultCameraZoom}
        defaultCameraHeading={defaultCameraHeading}
        selectedMarkerId={selectedMarkerId}
        onCameraLocationChange={onCameraLocationChange}
        onMapTap={onMapTap}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    height: '100%',
    width: '100%',
  },
});

export default RewardsMap;
